import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

/**
 * Registered UI panel: numeric id plus an optional reference to the host instance (component, element, etc.).
 *
 * @typeParam TRef - Narrow `ref` at registration or lookup sites (e.g. a specific panel component type).
 */
export interface Panel<T> {
  /** Monotonic-style id assigned by the registry; used for focus and ref lookup. */
  id: number;
  /** Optional handle to the live panel instance; cleared on unregister when expectations match. */
  ref?: T;
}

/**
 * State slice for {@link PanelRegistryStore}.
 */
export interface PanelRegistryState<T> {
  /** Panels keyed by {@link Panel.id} via NgRx Entity adapters. */
  panels: EntityState<Panel<T>>;
  /** Panel id that currently owns focus within the workspace, if any. */
  focusedPanel: number | undefined;
  /** Maximum number of panels allowed; `addPanel` is a no-op when the limit is reached. */
  maxNumPanels: number;
}

/**
 * NgRx ComponentStore that tracks mosaic-style panels, their optional host refs, and which panel is focused.
 *
 * @remarks
 * - Use {@link registerPanel} when ids are known up front; use {@link addPanel} to allocate the next id.
 * - Ref lifecycle: {@link setPanelRef} / {@link unregisterPanelRef} avoid retaining stale component instances.
 * - {@link focusedPanel$} is updated by {@link focusPanel}; consumers drive focus from shell or gallery code.
 */
export abstract class PanelRegistryStore<T> extends ComponentStore<PanelRegistryState<T>> {
  protected readonly panelAdapter: EntityAdapter<Panel<T>> = createEntityAdapter();

  constructor(initialState: PanelRegistryState<T>) {
    super(initialState);
  }

  /**
   * Emits panel ids whenever the collection changes; order matches registration order.
   */
  public readonly panelIds$ = this.select((state) => state.panels.ids as number[]);

  /**
   * Emits `true` when the panel count has reached the configured maximum.
   */
  public readonly maxPanelsReached$ = this.select(
    (state) => (state.panels.ids as number[]).length >= state.maxNumPanels
  );

  /**
   * Emits the entity dictionary (`id` → {@link Panel}) on each panels update.
   */
  public readonly panels$ = this.select((state) => state.panels.entities);

  /**
   * Emits the id of the focused panel, or `undefined` when none is focused.
   */
  public readonly focusedPanelRef$ = this.select((state) => state.panels.entities[state.focusedPanel]?.ref);

  public readonly firstPanelRef$ = this.select((state) => state.panels.entities[state.panels.ids[0]]?.ref);

  /**
   * Allocates a new panel with the next sequential id and no ref.
   *
   * @remarks Id is `0` when the registry is empty; otherwise `Number(lastId) + 1` where `lastId` is the final
   *   entry in the entity `ids` array (registration order), not necessarily the numeric maximum across gaps.
   */
  public readonly addPanel = this.updater((state) => {
    if ((state.panels.ids as number[]).length >= state.maxNumPanels) return state;

    return {
      ...state,
      panels: this.panelAdapter.addOne(
        {
          id: state.panels.ids.length > 0 ? Number(state.panels.ids[state.panels.ids.length - 1]) + 1 : 0,
        },
        state.panels
      ),
    };
  });

  /**
   * Inserts a new panel or replaces an existing one with the same `id`.
   *
   * @param panel - Full entity; include `ref` when the host is available in the same tick.
   */
  public readonly registerPanel = this.updater((state, panel: Panel<T>) => ({
    ...state,
    panels: this.panelAdapter.upsertOne(panel, state.panels),
  }));

  /**
   * Sets `ref` on an existing panel id.
   *
   * @param payload.panelId - Target panel; must already exist.
   * @param payload.ref - New host reference (replaces any previous ref).
   * @remarks Returns the prior state unchanged when `panelId` is unknown.
   */
  public readonly setPanelRef = this.updater((state, payload: { panelId: number; ref: T }) => {
    if (!state.panels.entities[payload.panelId]) {
      return state;
    }
    return {
      ...state,
      panels: this.panelAdapter.updateOne({ id: payload.panelId, changes: { ref: payload.ref } }, state.panels),
    };
  });

  /**
   * Clears `ref` for a panel when safe to do so.
   *
   * @param payload.panelId - Panel whose ref should be dropped.
   * @param payload.expectedRef - When set, the clear runs only if the stored ref is strictly equal to this value,
   *   preventing teardown of a newer instance that reused the same id.
   * @remarks No-op if the panel is missing, or if `expectedRef` is provided and does not match.
   */
  public readonly unregisterPanelRef = this.updater((state, payload: { panelId: number; expectedRef?: unknown }) => {
    const entity = state.panels.entities[payload.panelId];
    if (!entity) {
      return state;
    }
    if (payload.expectedRef !== undefined && entity.ref !== payload.expectedRef) {
      return state;
    }
    return {
      ...state,
      panels: this.panelAdapter.updateOne({ id: payload.panelId, changes: { ref: undefined } }, state.panels),
    };
  });

  /**
   * Removes a panel and its ref from the registry.
   * When the removed panel is the focused one, focus moves to the next panel in order (or the previous if last).
   *
   * @param panelId - Entity id to drop; ignored by the adapter if absent.
   */
  public readonly removePanel = this.updater((state, panelId: number) => {
    const panels = this.panelAdapter.removeOne(panelId, state.panels);

    if (state.focusedPanel !== panelId) {
      return { ...state, panels };
    }

    const remainingIds = panels.ids as number[];
    const oldIds = state.panels.ids as number[];
    const oldIndex = oldIds.indexOf(panelId);
    const nextFocused = remainingIds.length > 0
      ? remainingIds[Math.min(oldIndex, remainingIds.length - 1)]
      : undefined;

    return { ...state, panels, focusedPanel: nextFocused };
  });

  /**
   * Records which panel should be treated as focused (e.g. for keyboard or selection routing).
   *
   * @param panelId - Id to focus; does not validate existence in the registry.
   */
  public readonly focusedPanelId$ = this.select((state) => state.focusedPanel);

  /**
   * Records which panel should be treated as focused (e.g. for keyboard or selection routing).
   *
   * @param panelId - Id to focus; does not validate existence in the registry.
   */
  public readonly focusPanel = this.updater((state, panelId: number) => ({ ...state, focusedPanel: panelId }));

  /**
   * Cycles focus to the next panel in registration order, wrapping around to the first.
   */
  public readonly focusNextPanel = this.updater((state) => {
    const ids = state.panels.ids as number[];
    if (ids.length === 0) return state;

    const currentIndex = ids.indexOf(state.focusedPanel);
    const nextIndex = (currentIndex + 1) % ids.length;

    return { ...state, focusedPanel: ids[nextIndex] };
  });

  /**
   * Focuses the panel at the given 1-based position in registration order.
   * No-op if the position is out of range.
   */
  public readonly focusPanelByOrder = this.updater((state, position: number) => {
    const ids = state.panels.ids as number[];
    const index = position - 1;
    if (index < 0 || index >= ids.length) return state;
    return { ...state, focusedPanel: ids[index] };
  });

  /**
   * Cycles focus to the previous panel in registration order, wrapping around to the last.
   */
  public readonly focusPreviousPanel = this.updater((state) => {
    const ids = state.panels.ids as number[];
    if (ids.length === 0) return state;

    const currentIndex = ids.indexOf(state.focusedPanel);
    const prevIndex = (currentIndex - 1 + ids.length) % ids.length;

    return { ...state, focusedPanel: ids[prevIndex] };
  });
}
